import { useState } from 'react';
import { X, Send, Facebook, Linkedin, MessageCircle, Mail, Link } from 'lucide-react';
import { message } from 'antd';
import PropTypes from 'prop-types';

export default function ShareModal({ isOpen, onClose, title, url, description }) {
  const [copying, setCopying] = useState(false);

  if (!isOpen) return null;

  const shareOptions = [
    {
      name: 'Telegram',
      icon: Send,
      color: 'text-[#229ED9]',
      bgColor: 'bg-[#229ED9]/10',
      onClick: () => {
        const shareText = `${title}${description ? ` — ${description}` : ''}`;
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`;
        window.open(telegramUrl, '_blank', 'noopener,noreferrer');
      },
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'text-[#1877F2]',
      bgColor: 'bg-[#1877F2]/10',
      onClick: () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(facebookUrl, '_blank', 'noopener,noreferrer');
      },
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'text-[#0A66C2]',
      bgColor: 'bg-[#0A66C2]/10',
      onClick: () => {
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
      },
    },
    {
      name: 'Reddit',
      icon: MessageCircle,
      color: 'text-[#FF4500]',
      bgColor: 'bg-[#FF4500]/10',
      onClick: () => {
        const redditUrl = `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
        window.open(redditUrl, '_blank', 'noopener,noreferrer');
      },
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'text-[#25D366]',
      bgColor: 'bg-[#25D366]/10',
      onClick: () => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      },
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'text-gray-700 dark:text-gray-300',
      bgColor: 'bg-gray-100 dark:bg-[#0E0E0E]',
      onClick: () => {
        const subject = encodeURIComponent(title);
        const body = encodeURIComponent(`Check out this article:\n\n${title}\n\n${url}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      },
    },
  ];

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '-1000px';
    textArea.style.left = '-1000px';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const handleCopyLink = async () => {
    if (!url) {
      message.error('No link to copy');
      return;
    }

    setCopying(true);
    try {
      const canUseClipboardApi =
        typeof navigator !== 'undefined' &&
        !!navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function' &&
        typeof window !== 'undefined' &&
        window.isSecureContext;

      if (canUseClipboardApi) {
        await navigator.clipboard.writeText(url);
        message.success('Link copied to clipboard!');
        return;
      }

      const copied = fallbackCopyTextToClipboard(url);
      if (copied) {
        message.success('Link copied to clipboard!');
      } else {
        message.error('Copy failed (browser blocked clipboard)');
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      message.error('Failed to copy link');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-[#161616] shadow-2xl ring-1 ring-black/5 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
          <h3 id="share-modal-title" className="blog-title text-xl font-bold text-gray-950 dark:text-white">
            Share this post
          </h3>
          <button
            onClick={onClose}
            aria-label="Close share modal"
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#0E0E0E] hover:text-gray-700 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="grid grid-cols-3 sm:grid-cols-2 gap-3 mb-6">
            {shareOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.name}
                  onClick={() => {
                    option.onClick();
                    onClose();
                  }}
                  className={`flex flex-col items-center justify-center gap-2 py-3 px-2 rounded-xl ${option.bgColor} transition-all duration-200 hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900`}
                  aria-label={`Share on ${option.name}`}
                >
                  <IconComponent size={24} className={`${option.color} transition-transform group-hover:scale-110`} />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">
                    {option.name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Copy Link */}
          <div className="pt-4">
            <button
              onClick={handleCopyLink}
              disabled={copying}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-[#0E0E0E] text-gray-800 dark:text-gray-200 font-medium transition-colors hover:bg-gray-100 dark:hover:bg-[#1F1F1F] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              {copying ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Link size={20} />
              )}
              {copying ? 'Copying…' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

ShareModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  description: PropTypes.string,
};